import {FC, Fragment} from 'react'
import {Link, useLocation} from 'react-router-dom'

type Crumb = {title: string; to?: string}

// List-level pages (exact pathname) -> breadcrumb title.
const BREADCRUMB_TITLES: Record<string, string> = {
  '/roles/list': 'Manage Roles',
  '/buyers/list': 'Manage Buyer',
  '/users/list': 'Manage Users',
  '/allproducts/list': 'All Products',
  '/manage-product-listing/list': 'Manage Product Listings',
  '/aws/accounts': 'Amazon Accounts',
  '/amazon-product/list': 'Amazon Products',
  '/seller-product/list': 'Sellercloud Products',
  '/goflow-product/list': 'Goflow Products',
  '/channel-setting/list': 'Channel Settings',
  '/price-mapping/list': 'Price Mapping Settings',
  '/logs/list': 'Logs',
}

// Sections that have detail sub-pages: /<section>/<id>/<sub-page>
const DETAIL_SECTIONS: Record<string, {title: string; list: string}> = {
  allproducts: {title: 'All Products', list: '/allProducts/list'},
  'manage-product-listing': {title: 'Manage Product Listings', list: '/manage-product-listing/list'},
  buyers: {title: 'Manage Buyer', list: '/buyers/list'},
  users: {title: 'Manage Users', list: '/users/list'},
  'amazon-product': {title: 'Amazon Products', list: '/amazon-product/list'},
  'seller-product': {title: 'Sellercloud Products', list: '/seller-product/list'},
  'goflow-product': {title: 'Goflow Products', list: '/goflow-Product/list'},
}

const SUBPAGE_TITLES: Record<string, string> = {
  'products-detail': 'Product Detail',
  detail: 'Detail',
  feeds: 'Product Feeds',
  users: 'Assigned Users',
  activity: 'Activity',
  products: 'IBS Products',
  'amazon-products': 'Amazon Products',
  overview: 'Overview',
  settings: 'Settings',
}

// Dashboard drill-down pages: /dashboard/<slug> -> title
const DASHBOARD_TITLES: Record<string, string> = {
  'total-products': 'Total Products',
  'total-buyers': 'Total Buyers',
  'total-admins': 'Total Admins',
  'normalprice-productapproval': 'Normal Price - Product Approval',
  'saleprice-productapproval': 'Sale Price - Product Approval',
  'normalprice-atrisk': 'Normal Price - At Risk',
  'normalprice-atmargin': 'Normal Price - At Margin',
  'saleprice-atrisk': 'Sale Price - At Risk',
  'saleprice-atmargin': 'Sale Price - At Margin',
  'listing-errors': 'Listing Errors',
  'link-errors': 'Link Errors',
  'not-yet-listed': 'Not Yet Listed',
  'price-changes': 'Price Changes',
  'price-mismatches': 'Price Mismatches',
  'rating-changes': 'Rating Changes',
  'buybox-owner-changes': 'Buybox Owner Changes',
  'fulfillment-channel-changes': 'Amazon Fulfillment Changes',
  'buybox-suppressed': 'BuyBox Suppressed',
  'category-changes': 'Category Changes',
  'sales-rank-changes': 'Ranking Changes',
  'channel-inventory-feed-status-changes': 'Channel Inventory Feed Status Changes',
  'fba-fbm-adjusted-products': 'SFP Percentage for FBA/FBM-Adjusted Products',
}

const prettify = (segment: string) =>
  decodeURIComponent(segment)
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

const getCrumbs = (pathname: string): Crumb[] | null => {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return null

  const exact = BREADCRUMB_TITLES['/' + segments.join('/').toLowerCase()]
  if (exact) return [{title: exact}]

  if (segments[0].toLowerCase() === 'dashboard' && segments.length === 2) {
    const dashboardTitle = DASHBOARD_TITLES[segments[1].toLowerCase()]
    if (dashboardTitle) return [{title: 'Dashboard', to: '/dashboard'}, {title: dashboardTitle}]
  }

  const section = DETAIL_SECTIONS[segments[0].toLowerCase()]
  if (section && segments.length >= 2 && segments[1].toLowerCase() !== 'list') {
    const last = segments.length >= 3 ? segments[segments.length - 1] : ''
    const label = last ? SUBPAGE_TITLES[last.toLowerCase()] || prettify(last) : 'Details'
    return [{title: section.title, to: section.list}, {title: label}]
  }
  return null
}

const PageBreadcrumb: FC = () => {
  const {pathname} = useLocation()
  const crumbs = getCrumbs(pathname)
  if (!crumbs) return null

  const chevron = (
    <i
      className='bi bi-chevron-right fs-8 text-gray-500 mx-3 d-inline-flex align-items-center lh-1'
      style={{verticalAlign: 'middle'}}
    ></i>
  )

  return (
    <nav aria-label='breadcrumb' className='d-flex flex-wrap align-items-center fs-6 fw-semibold lh-1 mb-4'>
      <Link to='/dashboard' className='text-gray-500 text-hover-primary'>
        Home
      </Link>
      {crumbs.map((crumb, i) => (
        <Fragment key={`${crumb.title}-${i}`}>
          {chevron}
          {crumb.to ? (
            <Link to={crumb.to} className='text-gray-500 text-hover-primary'>
              {crumb.title}
            </Link>
          ) : (
            <span className='text-gray-900 fw-bold'>{crumb.title}</span>
          )}
        </Fragment>
      ))}
      {/* Slot where a page can portal an action (e.g. a Back button) to the right of the breadcrumb */}
      <div id='page-breadcrumb-actions' className='ms-auto d-flex align-items-center'></div>
    </nav>
  )
}

export {PageBreadcrumb}
